import React, { useState, useEffect, useCallback } from 'react';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';

import AdminHeader from './AdminHeader';
import QuizApi from '../QuizApi';

export default function AdminOrgSettings() {

  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);

  // ── Background color ──────────────────────────────────────────────
  const [bgColor, setBgColor] = useState('#7B7C6A');
  const [bgSaving, setBgSaving] = useState(false);
  const [bgSaved, setBgSaved] = useState(false);

  // ── Logo ─────────────────────────────────────────────────────────
  const [logoFile, setLogoFile] = useState(null);
  const [logoSaving, setLogoSaving] = useState(false);
  const [logoMsg, setLogoMsg] = useState(null);

  // ── Create user ───────────────────────────────────────────────────
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserFullName, setNewUserFullName] = useState('');
  const [createUserSaving, setCreateUserSaving] = useState(false);
  const [createUserResult, setCreateUserResult] = useState(null);

  // ── Delete user ───────────────────────────────────────────────────
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  // ── Invite producer ──────────────────────────────────────────────
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSaving, setInviteSaving] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [revokingInviteId, setRevokingInviteId] = useState(null);

  // ── Change password ───────────────────────────────────────────────
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await QuizApi.orgSettingsGet();
      if (!result.success) throw new Error(result.error || 'Fel vid hämtning');
      setSettings(result.response);
      setBgColor(result.response.backgroundCss || '#7B7C6A');
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveBgColor = useCallback(async () => {
    setBgSaving(true);
    setBgSaved(false);
    await QuizApi.orgSetBackgroundColor(bgColor);
    setBgSaving(false);
    setBgSaved(true);
    setTimeout(() => setBgSaved(false), 2500);
  }, [bgColor]);

  const uploadLogo = useCallback(async () => {
    if (!logoFile) return;
    setLogoSaving(true);
    setLogoMsg(null);
    const result = await QuizApi.orgSetLogo(logoFile);
    setLogoSaving(false);
    if (result.success) {
      setLogoFile(null);
      setLogoMsg({ ok: true, text: 'Loggan har laddats upp.' });
      load();
    } else {
      setLogoMsg({ ok: false, text: result.error || 'Uppladdning misslyckades.' });
    }
  }, [logoFile, load]);

  const removeLogo = useCallback(async () => {
    setLogoSaving(true);
    setLogoMsg(null);
    await QuizApi.orgRemoveLogo();
    setLogoSaving(false);
    setLogoMsg({ ok: true, text: 'Loggan har tagits bort.' });
    load();
  }, [load]);

  const createUser = useCallback(async () => {
    if (!newUserName.trim() || !newUserEmail.trim()) return;
    setCreateUserSaving(true);
    setCreateUserResult(null);
    const result = await QuizApi.orgUserCreate(newUserName.trim(), newUserEmail.trim(), newUserFullName.trim());
    setCreateUserSaving(false);
    if (result.success) {
      setCreateUserResult({ ok: true, user: result.response.user, password: result.response.generatedPassword });
      setNewUserName('');
      setNewUserEmail('');
      setNewUserFullName('');
      load();
    } else {
      setCreateUserResult({ ok: false, error: result.error || 'Kunde inte skapa användare.' });
    }
  }, [newUserName, newUserEmail, newUserFullName, load]);

  const deleteUser = useCallback(async (userRandom) => {
    if (!window.confirm('Ta bort användaren? Detta går inte att ångra.')) return;
    setDeletingUserId(userRandom);
    setDeleteError(null);
    const result = await QuizApi.orgUserDelete(userRandom);
    setDeletingUserId(null);
    if (result.success) {
      load();
    } else {
      setDeleteError(result.error || 'Kunde inte ta bort användaren.');
    }
  }, [load]);

  const sendInvite = useCallback(async () => {
    if (!inviteEmail.trim()) return;
    setInviteSaving(true);
    setInviteResult(null);
    const result = await QuizApi.orgInviteUserCreate(inviteEmail.trim());
    setInviteSaving(false);
    if (result.success) {
      setInviteResult({ ok: true, text: 'Inbjudan har skickats.' });
      setInviteEmail('');
      load();
    } else {
      const text = result.error === 'free_tier_producer_limit'
        ? `Gratiskontot tillåter max ${settings.producerLimit} Producers. Ta bort en befintlig Producer för att bjuda in en ny.`
        : (result.error || 'Kunde inte skicka inbjudan.');
      setInviteResult({ ok: false, text });
    }
  }, [inviteEmail, load, settings]);

  const revokeInvite = useCallback(async (inviteId) => {
    setRevokingInviteId(inviteId);
    await QuizApi.orgInviteRevoke(inviteId);
    setRevokingInviteId(null);
    load();
  }, [load]);

  const changePassword = useCallback(async () => {
    setPwMsg(null);
    if (!pwCurrent || !pwNew) { setPwMsg({ ok: false, text: 'Fyll i alla fält.' }); return; }
    if (pwNew !== pwConfirm) { setPwMsg({ ok: false, text: 'Lösenorden matchar inte.' }); return; }
    if (pwNew.length < 6) { setPwMsg({ ok: false, text: 'Nytt lösenord måste vara minst 6 tecken.' }); return; }
    setPwSaving(true);
    const result = await QuizApi.orgChangePassword(pwCurrent, pwNew);
    setPwSaving(false);
    if (result.success) {
      setPwMsg({ ok: true, text: 'Lösenordet har bytts.' });
      setPwCurrent(''); setPwNew(''); setPwConfirm('');
    } else {
      setPwMsg({ ok: false, text: result.error || 'Kunde inte byta lösenord.' });
    }
  }, [pwCurrent, pwNew, pwConfirm]);

  return (
    <div id="adminOrgSettings">
      <AdminHeader />
      <div className="adminContentWrapper">

        <Button
          variant="outline-secondary"
          size="sm"
          className="mb-3"
          onClick={() => window.history.back()}>
          Tillbaka
        </Button>

        <h5 className="mb-4">Organisationsinställningar</h5>

        {loading && <Spinner animation="border" />}
        {error && <Alert variant="danger">{error}</Alert>}

        {!loading && settings && (
          <>
            {/* ── Logga ── */}
            <section className="mb-4">
              <h6 className="font-weight-bold">Logga</h6>

              {settings.hasLogo && (
                <div className="d-flex align-items-center mb-2" style={{ gap: '12px' }}>
                  <img
                    src={`/api/org/${settings.orgId}/logo`}
                    alt="Nuvarande logga"
                    style={{ maxHeight: '60px', maxWidth: '200px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                  <Button variant="outline-danger" size="sm" disabled={logoSaving} onClick={removeLogo}>
                    Ta bort
                  </Button>
                </div>
              )}

              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <input
                  type="file"
                  accept="image/*"
                  className="form-control form-control-sm"
                  style={{ maxWidth: '280px' }}
                  onChange={e => { setLogoFile(e.target.files[0] || null); setLogoMsg(null); }}
                />
                <Button size="sm" className="btn-quizPrimaryDark" disabled={!logoFile || logoSaving} onClick={uploadLogo}>
                  {logoSaving ? <Spinner animation="border" size="sm" /> : 'Ladda upp'}
                </Button>
              </div>

              {logoMsg && (
                <Alert variant={logoMsg.ok ? 'success' : 'danger'} className="mt-2 py-1 px-2" style={{ fontSize: '0.85rem' }}>
                  {logoMsg.text}
                </Alert>
              )}
            </section>

            {/* ── Bakgrundsfärg ── */}
            <section className="mb-4">
              <h6 className="font-weight-bold">Bakgrundsfärg för quiz</h6>
              <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                <input
                  type="color"
                  value={bgColor}
                  onChange={e => { setBgColor(e.target.value); setBgSaved(false); }}
                  style={{ width: '48px', height: '36px', padding: '2px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}
                />
                <span className="text-muted small">{bgColor}</span>
                <Button size="sm" className="btn-quizPrimaryDark" disabled={bgSaving} onClick={saveBgColor}>
                  {bgSaving ? <Spinner animation="border" size="sm" /> : 'Spara'}
                </Button>
                {bgSaved && <span className="text-success small">✓ Sparat</span>}
              </div>
            </section>

            {/* ── Användare ── */}
            <section className="mb-4">
              <h6 className="font-weight-bold">Användare</h6>

              {deleteError && (
                <Alert variant="danger" className="py-1 px-2 mb-2" style={{ fontSize: '0.85rem' }}>
                  {deleteError}
                </Alert>
              )}

              <div className="adminTableScroll" style={{ maxWidth: '560px' }}>
                <table className="table table-sm table-hover mb-3">
                  <thead>
                    <tr>
                      <th>Användarnamn</th>
                      <th>Namn</th>
                      <th>E-post</th>
                      <th>Roll</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {settings.userList.map(u => (
                      <tr key={u.id}>
                        <td>{u.name}</td>
                        <td>{u.fullName || <span className="text-muted">—</span>}</td>
                        <td>{u.email || <span className="text-muted">—</span>}</td>
                        <td>{u.role}</td>
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            disabled={deletingUserId === u.id}
                            onClick={() => deleteUser(u.id)}>
                            {deletingUserId === u.id ? <Spinner animation="border" size="sm" /> : 'Ta bort'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {settings.userList.length === 0 && (
                      <tr><td colSpan={5} className="text-muted">Inga användare.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {!showCreateUser ? (
                <Button
                  size="sm"
                  className="btn-quizPrimaryDark"
                  onClick={() => { setShowCreateUser(true); setCreateUserResult(null); }}>
                  Skapa ny användare
                </Button>
              ) : (
                <div style={{ maxWidth: '400px' }}>
                  <div className="form-group mb-2">
                    <label className="small font-weight-bold">Användarnamn *</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={newUserName}
                      autoFocus
                      onChange={e => setNewUserName(e.target.value)}
                      placeholder="t.ex. anna"
                    />
                  </div>
                  <div className="form-group mb-2">
                    <label className="small font-weight-bold">E-post *</label>
                    <input
                      type="email"
                      className="form-control form-control-sm"
                      value={newUserEmail}
                      onChange={e => setNewUserEmail(e.target.value)}
                      placeholder="anna@exempel.se"
                    />
                  </div>
                  <div className="form-group mb-2">
                    <label className="small font-weight-bold">Fullständigt namn</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={newUserFullName}
                      onChange={e => setNewUserFullName(e.target.value)}
                      placeholder="Anna Andersson"
                    />
                  </div>
                  <div className="d-flex" style={{ gap: '6px' }}>
                    <Button
                      size="sm"
                      className="btn-quizPrimaryDark"
                      disabled={createUserSaving || !newUserName.trim() || !newUserEmail.trim()}
                      onClick={createUser}>
                      {createUserSaving ? <Spinner animation="border" size="sm" /> : 'Skapa'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => { setShowCreateUser(false); setCreateUserResult(null); }}>
                      Avbryt
                    </Button>
                  </div>

                  {createUserResult && createUserResult.ok && (
                    <Alert variant="success" className="mt-2 py-2 px-2" style={{ fontSize: '0.85rem' }}>
                      <strong>Användaren skapades!</strong><br />
                      Användarnamn: <strong>{createUserResult.user.name}</strong><br />
                      Temporärt lösenord: <strong>{createUserResult.password}</strong><br />
                      <span className="text-muted">Ge användaren detta lösenord. De kan byta det vid inloggning.</span>
                    </Alert>
                  )}
                  {createUserResult && !createUserResult.ok && (
                    <Alert variant="danger" className="mt-2 py-1 px-2" style={{ fontSize: '0.85rem' }}>
                      {createUserResult.error}
                    </Alert>
                  )}
                </div>
              )}
            </section>

            {/* ── Producers ── */}
            <section className="mb-4">
              <h6 className="font-weight-bold">Producers</h6>
              <p className="text-muted small mb-2">
                Bjud in en Producer via e-post. De kan skapa och köra quiz men inte hantera organisationen.
                {settings.producerLimit != null && (
                  <> Gratiskontot tillåter max <strong>{settings.producerLimit}</strong> Producers.</>
                )}
              </p>

              {settings.inviteList.length > 0 && (
                <div className="adminTableScroll mb-3" style={{ maxWidth: '480px' }}>
                  <table className="table table-sm table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Väntande inbjudan</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {settings.inviteList.map(i => (
                        <tr key={i.id}>
                          <td>{i.email}</td>
                          <td>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              disabled={revokingInviteId === i.id}
                              onClick={() => revokeInvite(i.id)}>
                              {revokingInviteId === i.id ? <Spinner animation="border" size="sm" /> : 'Återkalla'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="d-flex align-items-center" style={{ gap: '8px', maxWidth: '400px' }}>
                <input
                  type="email"
                  className="form-control form-control-sm"
                  value={inviteEmail}
                  onChange={e => { setInviteEmail(e.target.value); setInviteResult(null); }}
                  placeholder="producer@exempel.se"
                />
                <Button
                  size="sm"
                  className="btn-quizPrimaryDark"
                  disabled={inviteSaving || !inviteEmail.trim()}
                  onClick={sendInvite}>
                  {inviteSaving ? <Spinner animation="border" size="sm" /> : 'Bjud in'}
                </Button>
              </div>

              {inviteResult && (
                <Alert variant={inviteResult.ok ? 'success' : 'danger'} className="mt-2 py-1 px-2" style={{ fontSize: '0.85rem' }}>
                  {inviteResult.text}
                </Alert>
              )}
            </section>

            {/* ── Byt lösenord ── */}
            <section className="mb-4">
              <h6 className="font-weight-bold">Byt lösenord</h6>
              <div style={{ maxWidth: '340px' }}>
                <div className="form-group mb-2">
                  <label className="small font-weight-bold">Nuvarande lösenord</label>
                  <input
                    type="password"
                    className="form-control form-control-sm"
                    value={pwCurrent}
                    onChange={e => setPwCurrent(e.target.value)}
                    autoComplete="current-password"
                  />
                </div>
                <div className="form-group mb-2">
                  <label className="small font-weight-bold">Nytt lösenord</label>
                  <input
                    type="password"
                    className="form-control form-control-sm"
                    value={pwNew}
                    onChange={e => setPwNew(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
                <div className="form-group mb-2">
                  <label className="small font-weight-bold">Bekräfta nytt lösenord</label>
                  <input
                    type="password"
                    className="form-control form-control-sm"
                    value={pwConfirm}
                    onChange={e => setPwConfirm(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
                <Button
                  size="sm"
                  className="btn-quizPrimaryDark"
                  disabled={pwSaving}
                  onClick={changePassword}>
                  {pwSaving ? <Spinner animation="border" size="sm" /> : 'Byt lösenord'}
                </Button>
                {pwMsg && (
                  <Alert variant={pwMsg.ok ? 'success' : 'danger'} className="mt-2 py-1 px-2" style={{ fontSize: '0.85rem' }}>
                    {pwMsg.text}
                  </Alert>
                )}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
